export function detectPriceFormat(symbol: string | undefined, secType?: string | undefined) {
    // Normalize symbol by removing any non-alphanumeric characters so
    // variants like `BTC-USD`, `BTC/USD`, `BTC:USD` normalize to `BTCUSD`.
    const normalizedSymbol = String(symbol || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    const normalizedSecType = String(secType || '').trim().toUpperCase();
    const root = normalizedSymbol.match(/^([A-Z0-9]{1,6})/)?.[1] ?? normalizedSymbol;

    const precisionFromMinMove = (minMove: number) => {
        if (!Number.isFinite(minMove) || minMove <= 0) return 2;
        const asText = String(minMove);
        if (!asText.includes('.')) return 0;
        return asText.split('.')[1].length;
    };

    // Treat explicit cash/FX-coded symbols as high-precision by default
    if (normalizedSecType === 'CASH') {
        return { precision: 5, minMove: 0.00001 };
    }

    // If the symbol looks like a 6-letter FX pair (e.g. BTCUSD or EURUSD), prefer
    // crypto precision when either side is a known crypto root. This ensures BTC/USD
    // and similar pairs format with 2 decimals instead of 5.
    if (/^[A-Z]{6}$/.test(normalizedSymbol)) {
        const base = normalizedSymbol.slice(0, 3);
        const quote = normalizedSymbol.slice(3);
        const cryptoRoots = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);
        if (cryptoRoots.has(base) || cryptoRoots.has(quote)) {
            return { precision: 2, minMove: 0.01 };
        }
        return { precision: 5, minMove: 0.00001 };
    }

    if (normalizedSecType === 'CRYPTO') return { precision: 2, minMove: 0.01 };

    if (normalizedSecType === 'FUT' || normalizedSecType === 'FUTURES') {
        const futuresMinMoveByRoot: Record<string, number> = {
            '6E': 0.00005,
            '6B': 0.0001,
            '6A': 0.0001,
            '6C': 0.0001,
            '6J': 0.000001,
            '6N': 0.0001,
            '6S': 0.0001,
            'ES': 0.25,
            'MES': 0.25,
            'NQ': 0.25,
            'MNQ': 0.25,
            'RTY': 0.1,
            'M2K': 0.5,
            'YM': 1,
            'CL': 0.01,
            'GC': 0.1,
            'SI': 0.01,
        };
        const minMove = futuresMinMoveByRoot[root] ?? futuresMinMoveByRoot[normalizedSymbol] ?? 0.01;
        const precision = Math.max(2, precisionFromMinMove(minMove));
        return { precision, minMove };
    }

    return { precision: 2, minMove: 0.01 };
}

export function getPrecisionForSymbol(symbol?: string, secType?: string) {
    return detectPriceFormat(symbol, secType).precision;
}

export function formatPriceForSymbol(value: number, symbol?: string, secType?: string) {
    if (!Number.isFinite(Number(value))) return String(value ?? '—');
    const prec = getPrecisionForSymbol(symbol, secType);
    return Number(value).toFixed(prec);
}

export default formatPriceForSymbol;
